//-- Copyright (c) 2021 TMT Insights Group, LLC
import { Grid, Typography } from '@mui/material';
import { JobDetail } from '@/redux/slices/slice_types';
import { StatusColors } from '@tmt-insights/oms_fe_core/constants';
import { findandFormatUTCDateInString } from '@tmt-insights/oms_fe_core/date';
import {
  DateTimeDisplay,
  JobsEmbargoDate,
  JobsOverflowMenu,
  JobTypeLabel,
  JobRequirements,
  Notes,
  TableJobStage,
  UserDisplay
} from '@tmt-insights/oms_fe_core/columns';
import { AllTableColumnsConfig } from '@tmt-insights/oms_fe_core';
import TitleHierarchy from '../../components/TitleHierarchy';

const columns: AllTableColumnsConfig<JobDetail> = {
  hid: {
    label: 'Job ID',
    sortField: 'id',
    minWidth: 75,
    clickCopy: true,
    render: (cell: JobDetail['hid']) => (
      <Typography variant="subtitle1" sx={{ whiteSpace: 'nowrap' }}>
        {cell}
      </Typography>
    )
  },
  job_type: {
    label: 'Job Type',
    render: (cell: JobDetail['job_type']) => <JobTypeLabel type={cell} />
  },
  description: {
    label: 'Title',
    clickCopy: true,
    minWidth: 250,
    render: (_cell: any, job: JobDetail) => <TitleHierarchy title={job.title} nameFallback={job.description} />
  },
  version__external_id: {
    label: 'MCID',
    clickCopy: true,
    render: (cell: any) => <div style={{ whiteSpace: 'nowrap' }}>{cell || '-'}</div>
  },
  version__rightsline_id: {
    label: 'EPVID',
    clickCopy: true,
    minWidth: 100
  },
  version__phoenix7_id: {
    label: 'Phoenix 7 ID',
    clickCopy: true,
    minWidth: 100
  },
  category: {
    label: 'Category',
    minWidth: 200
  },
  version__name: {
    label: 'Version',
    clickCopy: true,
    minWidth: 200
  },
  output_edit_type: {
    label: 'Output Master Type',
    sortField: 'validated_workflow_inputs__edit_type'
  },
  order__managed_by: {
    label: 'Managed By',
    disableSort: true,
    minWidth: 100,
    render: (cell: JobDetail['order']['managed_by'], job: JobDetail) => (cell ? <UserDisplay user={cell} emptyText="-" /> : job.managed_by || '-')
  },
  customer: {
    label: 'Customer',
    clickCopy: true,
    minWidth: 180
  },
  assigned_to_vendor: {
    label: 'Assigned To Vendor',
    sortField: 'assigned_to_vendor',
    minWidth: 180,
    render: (cell: string | null) => cell || '-'
  },
  source_id: {
    label: 'Source ID',
    sortField: 'source_id_sort',
    clickCopy: true,
    minWidth: 180,
    render: (cell: string | null) => cell || '-'
  },
  profile: {
    label: 'Profile',
    clickCopy: true,
    sortField: 'job_profile',
    minWidth: 240,
    render: (cell: JobDetail['profile']) => findandFormatUTCDateInString(cell?.name)
  },
  due_date: {
    label: 'Due Date',
    clickCopy: true,
    // width: 100,
    render: (cell: JobDetail['due_date']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  created_at: {
    label: 'Created',
    clickCopy: true,
    // width: 100,
    render: (cell: JobDetail['created_at']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  completed_at: {
    label: 'Completed',
    clickCopy: true,
    // minWidth: 100,
    render: (cell: JobDetail['completed_at']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  stage: {
    label: 'Status',
    render: (cell: JobDetail['stage'], row: JobDetail) => (
      <Grid container rowSpacing={1}>
        <Grid item xs={12}>
          <TableJobStage
            status={row.status === 'cancelled' ? row.status : cell || row.status}
            colorOverride={StatusColors[row.status as keyof typeof StatusColors]}
          />
        </Grid>
        {row?.embargo_date && !row?.embargo_resumed_at && (
          <Grid item xs={12}>
            <JobsEmbargoDate job={row} />
          </Grid>
        )}
      </Grid>
    )
  },
  assets: {
    label: 'Requirements',
    render: (cell: JobDetail['assets'], row: JobDetail) => (
      <div style={{ opacity: row.status === 'cancelled' ? '0.5' : '1', marginLeft: -15 }}>
        <JobRequirements sources={cell} />
      </div>
    ),
    tooltip: {
      title: 'Displays sources received out of the total expected',
      placement: 'top'
    }
  },
  notes: {
    label: 'Notes',
    render: (_cell: any, row: JobDetail) => (
      <div style={{ width: 40, textAlign: 'center' }}>
        <Notes data={row} type="jobs" />
      </div>
    ),
    disableSort: true
  },
  overflow: {
    label: '',
    isOverflow: true,
    render: (_cell: any, job: JobDetail) => <JobsOverflowMenu row={job} />
  }
};

export default columns;
